/** @typedef {{ ruleIds: string[] }} QaFaultFiredDetail */

/**
 * Response header the API sets when a QA fault fired while serving that request (TENANT-5416).
 * The value is a comma-joined list of the rule ids that fired. The header is optional: an API
 * without fault injection never sends it, and its absence means nothing fired.
 */
export const QA_FAULTS_RESPONSE_HEADER = 'X-Lodgr-Qa-Faults'

export const QA_FAULT_FIRED_EVENT = 'lodgr:qa-fault-fired'

const RULE_ID_SEPARATOR = ','

/**
 * Split the header value into rule ids, dropping blanks.
 *
 * @param {string | null | undefined} headerValue
 * @returns {string[]}
 */
export const parseQaFaultsHeader = (headerValue) => {
    if (typeof headerValue !== 'string') {
        return []
    }
    return headerValue
        .split(RULE_ID_SEPARATOR)
        .map(ruleId => ruleId.trim())
        .filter(ruleId => ruleId.length > 0)
}

/**
 * Notify the QA overlay that one or more armed faults fired, so it can toast the tester
 * and refresh its active list.
 *
 * @param {string[]} ruleIds
 */
export const dispatchQaFaultFired = (ruleIds) => {
    if (!Array.isArray(ruleIds) || ruleIds.length === 0) {
        return
    }
    window.dispatchEvent(new CustomEvent(QA_FAULT_FIRED_EVENT, {
        detail: {
            ruleIds: [...ruleIds],
        },
    }))
}

/**
 * Per-response hook for the data layer: read the fault header and, when it names any rule,
 * dispatch the fired event. A response without the header (or without headers at all) is a no-op.
 *
 * @param {Response} response
 */
export const observeQaFaultsFromResponse = (response) => {
    if (typeof response?.headers?.get !== 'function') {
        return
    }
    dispatchQaFaultFired(parseQaFaultsHeader(response.headers.get(QA_FAULTS_RESPONSE_HEADER)))
}

/**
 * Subscribe to QA fault-fired notifications.
 *
 * @param {(ruleIds: string[]) => void} handler
 * @returns {() => void} Unsubscribe function
 */
export const subscribeToQaFaultFired = (handler) => {
    /** @param {CustomEvent<QaFaultFiredDetail>} event */
    const listener = (event) => {
        handler(Array.isArray(event.detail?.ruleIds) ? event.detail.ruleIds : [])
    }
    window.addEventListener(QA_FAULT_FIRED_EVENT, listener)
    return () => window.removeEventListener(QA_FAULT_FIRED_EVENT, listener)
}
